/**
 * Displays the publisher campaign config for a campaign.
 * Dynamically renders all fields. When "Show Draft" is toggled,
 * shows diff: current values struck through + draft values in blue.
 * If no current config exists but a draft does, shows draft values directly.
 */
import React, { useEffect, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Divider from '@amzn/meridian/divider';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { useChatPanel } from 'src/contexts/ChatPanelContext';
import { DraftPendingBanner } from 'src/components/campaigns/DraftPendingBanner';
import { type CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import {
  useCampaignPlansControllerFindByCampaignIdQuery,
  useCampaignPlansControllerUpdateStatusMutation,
  useCampaignsControllerGetQuery,
} from 'src/store/leela-api';
import { Logger } from 'src/utils/logger';

interface PublisherCampaignConfigTabProps {
  campaign: CampaignDesiredStateDto;
}

/**
 * Keys whose values are money in the account currency (e.g. "5" = $5.00).
 * Budgets and bids are stored in the account currency now; the backend converts to micros only
 * when building the Google payload. These are formatted with a "$" for display — NOT divided.
 */
const CURRENCY_FIELDS = new Set(['amount', 'targetCpc', 'targetCpa']);

/** Human-readable label from camelCase key */
function formatLabel (key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
}

/** Format a value for display. Currency fields (account-currency values) are shown with a "$". */
function formatValue (value: unknown, key?: string): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  const str = String(value);
  if (key && CURRENCY_FIELDS.has(key) && str) {
    const num = Number(str);
    if (!isNaN(num)) return `$${num.toFixed(2)}`;
  }
  return str;
}

const ConfigField: React.FC<{ label: string; current: string; draft?: string; showDraft: boolean }> = ({ label, current, draft, showDraft }) => {
  const changed = showDraft && draft !== undefined && draft !== current;
  const newField = showDraft && !current && draft;
  const sameDraftValue = showDraft && draft !== undefined && draft === current;
  return (
    <Row spacing="200" widths={['240px', 'fill']}>
      <Text color="secondary" type="b300">{label}</Text>
      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px' }}>
        {current && changed && (
          <Text color="secondary" type="b200">
            <span style={{ textDecoration: 'line-through' }}>{current}</span>
          </Text>
        )}
        {(current && !changed) && (
          <Text color={sameDraftValue ? 'secondary' : undefined} type="b300">{current}</Text>
        )}
        {(changed || newField) && (
          <Text type="b300">
            <span style={{ color: '#066098' }}>{draft}</span>
          </Text>
        )}
      </span>
    </Row>
  );
};

/**
 * Pulls the API's own explanation out of an RTK Query error.
 *
 * Approval failures are actionable ("adGroups is required (at least one)…"), so
 * showing a generic message throws away the only thing that tells the user what
 * to fix. Unlike the field-array shape in `RTKWrappedError`, this endpoint
 * returns `message` as a single string.
 */
function extractApiMessage (error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return undefined;
  const message = (data as { message?: unknown }).message;
  return typeof message === 'string' && message.trim().length > 0 ? message : undefined;
}

interface AdGroupCardProps {
  adGroup: Record<string, unknown>;
  draftAdGroup: Record<string, unknown> | undefined;
  idx: number;
  hasCurrentAdGroups: boolean;
  viewDraft: boolean;
  removedInDraft: boolean;
}

const AdGroupCard: React.FC<AdGroupCardProps> = ({ adGroup, draftAdGroup, idx, hasCurrentAdGroups, viewDraft, removedInDraft }) => {
  const { t } = useTranslation();
  const agKeys = Array.from(new Set([...Object.keys(adGroup), ...(draftAdGroup ? Object.keys(draftAdGroup) : [])]));
  const currentAds = (adGroup.adConfigs || []) as Array<Record<string, unknown>>;
  const draftAds = (draftAdGroup?.adConfigs || []) as Array<Record<string, unknown>>;
  const ads = (() => {
    if (viewDraft && draftAds.length > currentAds.length) return draftAds;
    if (currentAds.length > 0) return currentAds;
    return viewDraft ? draftAds : [];
  })();

  return (
    <Box spacingInset="400" type="fill">
      <Column spacing="300">
        <Row alignmentVertical="center" spacing="200">
          <Text type="h100">
            {removedInDraft
              ? <span style={{ textDecoration: 'line-through' }}>{(adGroup.name ?? `Ad Group ${idx + 1}`) as string}</span>
              : (adGroup.name ?? draftAdGroup?.name ?? `Ad Group ${idx + 1}`) as string}
          </Text>
          {removedInDraft && (
            <Text color="secondary" type="b200">{t('publisher_campaign_config_not_in_draft')}</Text>
          )}
        </Row>
        {agKeys.filter(k => k !== 'name' && k !== 'adConfigs' && k !== 'adGroupConfigId').map(key => {
          const current = hasCurrentAdGroups ? formatValue(adGroup[key], key) : '';
          const draft = draftAdGroup ? formatValue(draftAdGroup[key], key) : (!hasCurrentAdGroups ? formatValue(adGroup[key], key) : undefined);
          if (!current && (!draft || !viewDraft)) return null;
          return <ConfigField current={current} draft={draft} key={key} label={formatLabel(key)} showDraft={viewDraft} />;
        })}
        {ads.length > 0 && (
          <>
            <Text type="h100">Ads ({ads.length})</Text>
            {ads.map((ac, acIdx) => {
              const draftAc = hasCurrentAdGroups ? (draftAdGroup?.adConfigs as Array<Record<string, unknown>> | undefined)?.[acIdx] : undefined;
              return <AdConfigCard ac={ac} draftAc={draftAc} hasCurrentAdGroups={hasCurrentAdGroups} key={acIdx} viewDraft={viewDraft} />;
            })}
          </>
        )}
      </Column>
    </Box>
  );
};

interface AdConfigCardProps {
  ac: Record<string, unknown>;
  draftAc: Record<string, unknown> | undefined;
  hasCurrentAdGroups: boolean;
  viewDraft: boolean;
}

const AdConfigCard: React.FC<AdConfigCardProps> = ({ ac, draftAc, hasCurrentAdGroups, viewDraft }) => {
  const adConfigSource = hasCurrentAdGroups ? ac : undefined;
  const acKeys = Array.from(new Set([...Object.keys(ac), ...(draftAc ? Object.keys(draftAc) : [])]));
  return (
    <Box spacingInset="400" type="outline">
      <Column spacing="200">
        <Text tag="strong" type="b300">{(ac.adType || draftAc?.adType) as string}</Text>
        {acKeys.filter(k => k !== 'adType').map(key => {
          const current = adConfigSource ? formatValue(adConfigSource[key], key) : '';
          const draft = draftAc ? formatValue(draftAc[key], key) : formatValue(ac[key], key);
          if (!current && (!draft || !viewDraft)) return null;
          return <ConfigField current={current} draft={draft} key={key} label={formatLabel(key)} showDraft={viewDraft} />;
        })}
      </Column>
    </Box>
  );
};

export const PublisherCampaignConfigTab: React.FC<PublisherCampaignConfigTabProps> = ({ campaign }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [viewDraft, setViewDraft] = useState(false);
  const publishInFlightRef = useRef(false);
  const { open: chatOpen } = useChatPanel();
  const [updateCampaignPlanStatus, { isLoading: isPublishing }] = useCampaignPlansControllerUpdateStatusMutation();
  const {
    currentData: queriedActiveDraft,
    error: activeDraftError,
    refetch: refetchDraft,
  } = useCampaignPlansControllerFindByCampaignIdQuery(
    { campaignId: campaign.campaignId! },
    { skip: !campaign.campaignId, pollingInterval: chatOpen ? 30_000 : undefined },
  );
  const { refetch: refetchCampaign, isFetching } = useCampaignsControllerGetQuery(
    { id: campaign.campaignId! },
    { skip: !campaign.campaignId },
  );
  // Approving a draft leaves no IN_PROGRESS plan, so the query 404s and RTK Query
  // retains the last-good data — that retained draft must be discarded. Any OTHER
  // error (500, network) is transient: keep showing the draft rather than pretending
  // the campaign has none, which would hide the publish action.
  const draftNotFound = (activeDraftError as { status?: number } | undefined)?.status === 404;
  const activeDraft = draftNotFound ? undefined : queriedActiveDraft;

  useEffect(() => {
    if (chatOpen && activeDraft) setViewDraft(true);
  }, [chatOpen, activeDraft]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentConfig: Record<string, any> = (campaign.publisherCampaignConfig as Record<string, unknown>) ?? {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const draftConfig: Record<string, any> | null = activeDraft
    ? (activeDraft as unknown as { publisherCampaignConfig: Record<string, unknown> }).publisherCampaignConfig as Record<string, unknown>
    : null;

  const hasCurrentConfig = campaign.publisherCampaignConfig && Object.keys(currentConfig).length > 0;
  const hasDraft = !!draftConfig && Object.keys(draftConfig).length > 0;
  const showDraftDiff = viewDraft && hasDraft;

  useEffect(() => {
    // Leave draft view when the draft goes away
    if (!hasDraft) setViewDraft(false);
  }, [hasDraft]);

  const handlePublish = async (): Promise<void> => {
    if (publishInFlightRef.current) return;
    publishInFlightRef.current = true;

    try {
      if (!activeDraft?.id) {
        createToast({
          type: AlertType.ERROR,
          message: t('publisher_campaign_config_publish_error'),
        });
        Logger.error('Cannot publish campaign draft without an id', { campaignId: campaign.campaignId });
        return;
      }

      const response = await updateCampaignPlanStatus({
        id: activeDraft.id,
        updateStatusDto: { status: 'APPROVED' },
      });

      if ('data' in response) {
        setViewDraft(false);
        createToast({
          type: AlertType.SUCCESS,
          message: t('publisher_campaign_config_publish_success'),
        });
        await refetchCampaign();
      } else {
        createToast({
          type: AlertType.ERROR,
          message: extractApiMessage(response.error) ?? t('publisher_campaign_config_publish_error'),
        });
        Logger.error('Failed to publish campaign draft', {
          campaignId: campaign.campaignId,
          error: response.error,
        });
      }
    } finally {
      publishInFlightRef.current = false;
    }
  };

  // Collect all keys from both current and draft
  const allKeys = Array.from(new Set([...Object.keys(currentConfig), ...(draftConfig ? Object.keys(draftConfig) : [])]));
  const scalarKeys = allKeys.filter(k => {
    // budget, adGroups, and biddingStrategy each have a dedicated section below.
    // biddingStrategy is excluded here even though a legacy value may be a bare
    // string (pre object-union migration) — otherwise it would render twice.
    if (k === 'budget' || k === 'adGroups' || k === 'biddingStrategy') return false;
    const currentValue = currentConfig[k];
    const draftValue = draftConfig?.[k];
    if (Array.isArray(currentValue) || Array.isArray(draftValue)) return false;
    if ((currentValue !== null && currentValue !== undefined && typeof currentValue === 'object') || (draftValue !== null && draftValue !== undefined && typeof draftValue === 'object')) return false;
    return true;
  });
  const hasCurrentAdGroups = !!currentConfig.adGroups?.length;
  // Approving a draft REPLACES the campaign's publisherCampaignConfig wholesale
  // (campaigns.service.ts `$set` of the whole object) — the deep merge applies only
  // to plan updates, not to publishing. So anything the draft lacks is lost on
  // publish, whether the key is absent or explicitly empty. Both render as removals,
  // consistent with how absent scalar fields already render struck-through.
  const currentAdGroups = currentConfig.adGroups || [];
  const draftAdGroups = draftConfig?.adGroups || [];
  // Show the larger set when draft view is on; fall back to draft if no current
  const adGroups = (() => {
    if (showDraftDiff && draftAdGroups.length > currentAdGroups.length) return draftAdGroups;
    if (currentAdGroups.length > 0) return currentAdGroups;
    return showDraftDiff ? draftAdGroups : [];
  })();

  return (
    <Box spacingInset="500" type="outline">
      <Column spacing="400">
        {/* Refresh must remain available when stale data temporarily produces the empty state. */}
        <Row alignmentVertical="top" spacing="300" widths={['fill', 'fit']}>
          <Text type="h200">{t('publisher_config_campaign_settings')}</Text>
          <Column alignmentHorizontal="center" spacing="100">
            <Button disabled={isFetching} label={t('publisher_campaign_config_refresh')} size="small" type="icon" onClick={() => { refetchCampaign(); refetchDraft(); }}><Icon tokens={refreshTokens} /></Button>
            <Text color="secondary" type="b100">{t('publisher_campaign_config_refresh')}</Text>
          </Column>
        </Row>
        <Divider />

        {!hasCurrentConfig && !hasDraft
          ? (
            <Column alignmentHorizontal="center" spacing="400">
              <Text color="secondary" type="b300">{t('publisher_campaign_config_empty')}</Text>
            </Column>
            )
          : (
            <>
              {hasDraft && (
                <DraftPendingBanner
                  isPublishing={isPublishing}
                  updatedAt={activeDraft?.updatedAt}
                  viewDraft={viewDraft}
                  onPublish={handlePublish}
                  onToggleDraft={() => { setViewDraft(!viewDraft); }}
                />
              )}

              {!hasCurrentConfig && hasDraft && !showDraftDiff && (
                <Text color="secondary" type="b300">{t('publisher_campaign_config_no_published')}</Text>
              )}

              {/* Scalar fields */}
              <Column spacing="200">
                {scalarKeys.map(key => {
                  const current = formatValue(currentConfig[key], key);
                  const draft = draftConfig ? formatValue(draftConfig[key], key) : undefined;
                  if (!current && (!draft || !showDraftDiff)) return null;
                  return <ConfigField current={current} draft={draft} key={key} label={formatLabel(key)} showDraft={showDraftDiff} />;
                })}
              </Column>

              {/* Budget (special formatting) */}
              {(currentConfig.budget || (draftConfig?.budget && showDraftDiff)) && (
                <>
                  <Text type="h200">Budget</Text>
                  <Divider />
                  <Column spacing="200">
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
                    {Object.keys({ ...currentConfig.budget, ...(showDraftDiff ? draftConfig?.budget : {}) }).map(key => {
                      const current = formatValue(currentConfig.budget?.[key], key);
                      // With a draft present, an absent budget (or absent member) is a
                      // removal on publish, not "unchanged" — mirror the adGroups handling.
                      const draft = showDraftDiff
                        ? formatValue(draftConfig?.budget?.[key], key)
                        : undefined;
                      if (!current && (!draft || !showDraftDiff)) return null;
                      return (
                        <ConfigField
                          current={current}
                          draft={draft}
                          key={key}
                          label={formatLabel(key)}
                          showDraft={showDraftDiff}
                        />);
                    })}
                  </Column>
                </>
              )}

              {/* Bidding Strategy (object: type + optional targetCpc/targetCpa in the account currency).
                  Legacy campaigns persisted biddingStrategy as a bare string enum (pre object-union
                  migration); normalize a string to { type } so it renders as one row, not one row
                  per character (spreading a string yields index keys). */}
              {(currentConfig.biddingStrategy || (draftConfig?.biddingStrategy && showDraftDiff)) && (() => {
                const normalizeStrategy = (s: unknown): Record<string, unknown> =>
                  typeof s === 'string' ? { type: s } : ((s as Record<string, unknown>) ?? {});
                const currentStrategy = normalizeStrategy(currentConfig.biddingStrategy);
                const draftStrategy = showDraftDiff ? normalizeStrategy(draftConfig?.biddingStrategy) : {};
                return (
                  <>
                    <Text type="h200">Bidding Strategy</Text>
                    <Divider />
                    <Column spacing="200">
                      {Object.keys({ ...currentStrategy, ...draftStrategy }).map(key => {
                        const current = formatValue(currentStrategy[key], key);
                        const draft = showDraftDiff
                          ? formatValue(draftStrategy[key], key)
                          : undefined;
                        if (!current && (!draft || !showDraftDiff)) return null;
                        return (
                          <ConfigField
                            current={current}
                            draft={draft}
                            key={key}
                            label={formatLabel(key)}
                            showDraft={showDraftDiff}
                          />);
                      })}
                    </Column>
                  </>
                );
              })()}

              {/* Target Devices (array: MOBILE/DESKTOP/TABLET/CONNECTED_TV). Rendered here
                  because the scalar section above excludes arrays; formatValue joins the list. */}
              {((currentConfig.targetDevices?.length ?? 0) > 0 || (showDraftDiff && (draftConfig?.targetDevices?.length ?? 0) > 0)) && (
                <>
                  <Text type="h200">Device Targeting</Text>
                  <Divider />
                  <Column spacing="200">
                    <ConfigField
                      current={formatValue(currentConfig.targetDevices, 'targetDevices')}
                      draft={showDraftDiff ? formatValue(draftConfig?.targetDevices, 'targetDevices') : undefined}
                      label={formatLabel('targetDevices')}
                      showDraft={showDraftDiff}
                    />
                  </Column>
                </>
              )}

              {/* Ad Groups */}
              {adGroups.length > 0 && (
                <>
                  <Text type="h200">{`Ad Groups (${showDraftDiff ? draftAdGroups.length : currentAdGroups.length})`}</Text>
                  <Divider />
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {adGroups.map((adGroup: any, idx: number) => {
                    const draftAdGroup = draftConfig?.adGroups?.find(
                      (draftGroup: Record<string, unknown>) => draftGroup.adGroupConfigId && draftGroup.adGroupConfigId === adGroup.adGroupConfigId
                    ) ?? draftConfig?.adGroups?.[idx];
                    return (
                      <AdGroupCard
                        adGroup={adGroup}
                        draftAdGroup={draftAdGroup}
                        hasCurrentAdGroups={hasCurrentAdGroups}
                        idx={idx}
                        key={adGroup.adGroupConfigId ?? idx}
                        removedInDraft={showDraftDiff && !draftAdGroup}
                        viewDraft={showDraftDiff}
                      />
                    );
                  })}
                </>
              )}
            </>
            )}
      </Column>
    </Box>
  );
};
